import { useState, useEffect } from "react";
import { getFlights, deleteFlight, restoreFlight, bulkDeleteCanceledFlights } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plane, ArrowRight, Calendar, Loader2, XCircle, User, Clock, Trash2, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionContext";

export default function CanceledFlightsPage() {
  const { hasPermission } = usePermissions();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFlightInfo, setDeleteFlightInfo] = useState(null);
  const [restoreId, setRestoreId] = useState(null);
  const [restoreFlightInfo, setRestoreFlightInfo] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [expandedFlights, setExpandedFlights] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Permission checks
  const canRestore = hasPermission("canceled_flights", "restore");
  const canDeletePermanently = hasPermission("canceled_flights", "delete_permanently");

  useEffect(() => {
    fetchFlights();
  }, []);

  const fetchFlights = async () => {
    try {
      const response = await getFlights({ status: "canceled" });
      const sorted = [...response.data].sort((a, b) => {
        const aKey = `${a.flight_date || ""} ${a.time || ""}`;
        const bKey = `${b.flight_date || ""} ${b.time || ""}`;
        return bKey.localeCompare(aKey);
      });
      setFlights(sorted);
    } catch (error) {
      console.error("Failed to fetch canceled flights:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (flightId) => {
    setExpandedFlights(prev => {
      const newSet = new Set(prev);
      if (newSet.has(flightId)) {
        newSet.delete(flightId);
      } else {
        newSet.add(flightId);
      }
      return newSet;
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteFlight(deleteId);
      toast.success("Flight record deleted permanently");
      setFlights(prev => prev.filter(f => f.id !== deleteId));
      setDeleteId(null);
      setDeleteFlightInfo(null);
    } catch (error) {
      toast.error("Failed to delete flight");
    }
  };

  const handleRestore = async () => {
    if (!restoreId) return;
    setRestoring(true);
    try {
      await restoreFlight(restoreId);
      toast.success("Flight restored successfully");
      setFlights(prev => prev.filter(f => f.id !== restoreId));
      setRestoreId(null);
      setRestoreFlightInfo(null);
    } catch (error) {
      toast.error("Failed to restore flight");
    } finally {
      setRestoring(false);
    }
  };

  const openDeleteDialog = (e, flight) => {
    e.stopPropagation();
    setDeleteId(flight.id);
    setDeleteFlightInfo(flight);
  };

  const openRestoreDialog = (e, flight) => {
    e.stopPropagation();
    setRestoreId(flight.id);
    setRestoreFlightInfo(flight);
  };

  const toggleSelect = (e, flightId) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(flightId)) next.delete(flightId);
      else next.add(flightId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === flights.length) return new Set();
      return new Set(flights.map(f => f.id));
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await bulkDeleteCanceledFlights(ids);
      toast.success(`${res.data.deleted || ids.length} flight(s) deleted permanently`);
      setFlights(prev => prev.filter(f => !selectedIds.has(f.id)));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete selected flights");
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="canceled-flights-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Canceled Flights</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">View all canceled flight records</p>
        </div>
        <Badge variant="secondary" className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-lg px-4 py-2">
          {flights.length} Canceled
        </Badge>
      </div>

      {/* Bulk actions */}
      {canDeletePermanently && flights.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 bg-[#1e293b] border border-gray-700 rounded-lg px-4 py-3 mb-4" data-testid="canceled-bulk-actions">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === flights.length}
              onCheckedChange={toggleSelectAll}
              className="border-gray-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              data-testid="select-all-canceled"
            />
            <span className="text-sm text-gray-300">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedIds.size === 0}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="bulk-delete-canceled"
          >
            <Trash2 size={14} className="mr-2" />
            Delete Selected ({selectedIds.size})
          </Button>
        </div>
      )}

      {/* Flights List */}
      {flights.length === 0 ? (
        <div className="empty-state">
          <XCircle className="mx-auto text-[#64748b]" size={64} />
          <h3>No Canceled Flights</h3>
          <p>There are no canceled flights to display.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {flights.map((flight, index) => {
            const isExpanded = expandedFlights.has(flight.id);
            
            return (
              <Card
                key={flight.id}
                className="border-l-4 border-l-red-500 bg-[#1e293b] dark:bg-[#1e293b] hover:bg-[#263548] dark:hover:bg-[#263548] transition-colors cursor-pointer"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`canceled-flight-${flight.id}`}
                onClick={() => toggleExpand(flight.id)}
              >
                <CardContent className="p-0">
                  {/* Collapsed Header Row - Always Visible */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-4">
                    {/* Left: Flight Info */}
                    <div className="flex items-center gap-4">
                      {canDeletePermanently && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(flight.id)}
                            onCheckedChange={() => toggleSelect({ stopPropagation: () => {} }, flight.id)}
                            className="border-gray-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                            data-testid={`select-canceled-${flight.id}`}
                          />
                        </div>
                      )}
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <XCircle size={22} className="text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-mono font-bold text-white text-lg">{flight.registration || flight.flight_number}</p>
                          <Badge className="bg-red-600 text-white text-xs">CANCELED</Badge>
                        </div>
                        <p className="text-sm text-gray-400">
                          {flight.aircraft_type || "N/A"} • {flight.registration || "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Center: Route */}
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Origin</p>
                        <p className="font-bold text-white text-lg line-through decoration-red-500">{flight.origin}</p>
                      </div>
                      <div className="w-12 h-[2px] bg-red-500/50 relative">
                        <ArrowRight size={18} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-red-400" />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Destination</p>
                        <p className="font-bold text-white text-lg line-through decoration-red-500">{flight.destination}</p>
                      </div>
                    </div>

                    {/* Right: Date & Time + Expand Icon */}
                    <div className="flex items-center gap-4">
                      <div className="text-left sm:text-right">
                        <div className="flex items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0">
                          <p className="font-semibold text-white text-base sm:text-lg">
                            {format(parseISO(flight.flight_date), "dd MMM yyyy")}
                          </p>
                          <p className="font-mono text-gray-400">{flight.time || "--:--"}</p>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-700 px-5 pb-5 animate-fade-in">
                      {/* Cancellation Details */}
                      <div className="mt-4 p-4 bg-red-900/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <XCircle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold text-red-400 mb-1">Cancellation Reason:</p>
                            <p className="text-red-300">{flight.cancel_reason || "No reason provided"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 mt-3 text-sm text-red-400">
                          <div className="flex items-center gap-1">
                            <User size={14} />
                            <span>Canceled by: {flight.canceled_by || "Unknown"}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            <span>
                              {flight.canceled_at 
                                ? format(parseISO(flight.canceled_at), "dd MMM yyyy HH:mm")
                                : "Unknown time"
                              }
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Original Flight Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 pt-4 mt-4 border-t border-gray-700">
                        <div>
                          <p className="text-xs text-gray-500">Type</p>
                          <p className="font-medium text-white">{flight.flight_type || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Operator</p>
                          <p className="font-medium text-white">{flight.operator || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">PAX In</p>
                          <p className="font-medium text-white">{flight.pax_in || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">PAX Out</p>
                          <p className="font-medium text-white">{flight.pax_out || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Parking</p>
                          <p className="font-medium text-white">{flight.parking_position || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Created by</p>
                          <p className="font-medium text-white">{flight.created_by || "N/A"}</p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-700">
                        {canRestore && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openRestoreDialog(e, flight)}
                            className="text-green-400 hover:text-green-300 hover:bg-green-900/30 border-green-600 bg-transparent"
                            data-testid={`restore-canceled-${flight.id}`}
                          >
                            <RotateCcw size={16} className="mr-2" />
                            Restore Flight
                          </Button>
                        )}
                        {canDeletePermanently && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openDeleteDialog(e, flight)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30 border-red-600 bg-transparent"
                            data-testid={`delete-canceled-${flight.id}`}
                          >
                            <Trash2 size={16} className="mr-2" />
                            Delete Permanently
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={!!restoreId} onOpenChange={() => { setRestoreId(null); setRestoreFlightInfo(null); }}>
        <AlertDialogContent className="bg-[#1e293b] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="text-green-500" size={20} />
              Restore Flight
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to restore this canceled flight back to active status?
              {restoreFlightInfo && (
                <div className="mt-3 p-3 bg-green-900/30 rounded-lg">
                  <p className="font-mono font-bold text-green-400">{restoreFlightInfo.flight_number}</p>
                  <p className="text-sm text-green-500">
                    {restoreFlightInfo.origin} → {restoreFlightInfo.destination} • {restoreFlightInfo.flight_date}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 bg-transparent text-gray-300 hover:bg-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoring}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteFlightInfo(null); }}>
        <AlertDialogContent className="bg-[#1e293b] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="text-red-500" size={20} />
              Delete Flight Record
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to permanently delete this canceled flight record?
              {deleteFlightInfo && (
                <div className="mt-3 p-3 bg-red-900/30 rounded-lg">
                  <p className="font-mono font-bold text-red-400">{deleteFlightInfo.flight_number}</p>
                  <p className="text-sm text-red-500">
                    {deleteFlightInfo.origin} → {deleteFlightInfo.destination} • {deleteFlightInfo.flight_date}
                  </p>
                </div>
              )}
              <p className="mt-2 text-red-400 font-medium">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 bg-transparent text-gray-300 hover:bg-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="bg-[#1e293b] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="text-red-500" size={20} />
              Delete {selectedIds.size} Flight{selectedIds.size !== 1 ? "s" : ""}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to permanently delete <span className="text-red-400 font-semibold">{selectedIds.size}</span> selected canceled flight record{selectedIds.size !== 1 ? "s" : ""}?
              <p className="mt-2 text-red-400 font-medium">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 bg-transparent text-gray-300 hover:bg-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-bulk-delete"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
